//LIBRARIES
import React from 'react';
import { connect } from 'react-redux';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { SelectField } from 'redux-form-material-ui';
import { DatePicker } from 'redux-form-material-ui';
import MenuItem from 'material-ui/MenuItem';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

//HELPERS
import TextField from 'helpers/reduxFormMaterialUiNext/TextField';
import { BooleanRadioButtons } from 'helpers/BooleanRadioButtons';
import * as validator from 'helpers/Validator';
import { toFriendlyDateString } from 'helpers/Formatter';
import { vendorCategory } from 'helpers/SelectOptions';
import ReactPhoneInput from 'helpers/reduxFormMaterialUiNext/ReactPhoneInput';
import OutlinedSelect from 'helpers/reduxFormMaterialUiNext/OutlinedSelect';
import { getTranslatedString } from 'helpers/i18n';

//COMPONENTS
import AddressAutocomplete from 'components/shared/addressAutocomplete';

//CONSTANTS
import { CANADIAN_PROVINCES, USA_STATES } from 'constants/address.constants';

//STYLINGS
const style = {
  header: {
    margin: '5px -20px 10px -20px',
    backgroundColor: '#f1f1f1',
    padding: '5px 10px',
    fontSize: '14px',
    fontWeight: '800',
  },
};

const validateInsuranceNumber = validator.requiredIfExists('insurance_expiry_date');
const validateInsuranceExpiry = validator.requiredIfExists('insurance_number');

let AddVendorForm = class extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentCountry: getTranslatedString('country.CA'),
    };
  }

  componentDidUpdate() {
    const { country } = this.props;
    const { currentCountry } = this.state;

    if (country) {
      if (!isEqual(country, currentCountry)) {
        this.setState({ currentCountry: country });
      }
    }
  }

  render() {
    const { handleSubmit } = this.props;

    return (
      <div>
        <form onSubmit={handleSubmit.bind(this)}>
          {this.renderVendorDetails()}
          {this.renderVendorInsurance()}
          {this.renderVendorAddress()}
        </form>
      </div>
    );
  }

  renderVendorDetails() {
    return (
      <div>
        <div className="row" style={style.header}>
          <div className="col-xs-12">
            <p style={{ margin: '0', textAlign: 'center' }}>
              {' '}
              {getTranslatedString('landlordPortal.vendors.vendorDetails')}{' '}
            </p>
          </div>
        </div>

        <div className="row" style={{ marginTop: '20px' }}>
          <div className="col-sm-6" style={{ marginBottom: '20px' }}>
            <Field
              name="name"
              component={TextField}
              label={getTranslatedString('landlordPortal.vendors.vendorName')}
              validate={[validator.required]}
              multiLine={false}
              fullWidth
              variant="outlined"
              style={{ fontSize: '14px' }}
            />
          </div>

          <div className="col-sm-6" style={{ marginBottom: '20px' }}>
            <Field
              name="classification"
              component={OutlinedSelect}
              style={{ fontSize: '14px' }}
              label={getTranslatedString('landlordPortal.vendors.classification')}
              fullWidth
              iconStyle={{ marginLeft: '20px !important' }}
            >
              {vendorCategory.map((vendorType) => (
                <MenuItem key={`v_type_${vendorType}`} value={vendorType}>
                  {vendorType}
                </MenuItem>
              ))}
            </Field>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-6" style={{ marginBottom: '20px' }}>
            <Field
              name="contact_number"
              component={ReactPhoneInput}
              label={getTranslatedString('landlordPortal.vendors.contactNumber')}
              validate={[validator.required, validator.validInternationalPhoneNumber]}
              multiLine={false}
              fullWidth
              style={{ fontSize: '14px' }}
            />
          </div>

          <div className="col-sm-6" style={{ marginBottom: '20px' }}>
            <Field
              name="contact_email"
              component={TextField}
              label={getTranslatedString('landlordPortal.vendors.contactEmail')}
              variant="outlined"
              validate={[validator.validEmail]}
              multiLine={false}
              fullWidth
              style={{ fontSize: '14px' }}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-xs-12" style={{ marginBottom: '20px' }}>
            <Field
              name="notes"
              component={TextField}
              label={getTranslatedString('landlordPortal.vendors.notes')}
              variant="outlined"
              multiLine={true}
              fullWidth
              rows={1}
              style={{ fontSize: '14px' }}
            />
          </div>
        </div>

        <div className="row" style={{ marginTop: '15px' }}>
          <div style={{ textAlign: 'center', marginTop: '10px' }}>
            <div style={{ fontSize: '14px' }}>
              {getTranslatedString('landlordPortal.vendors.onsite')}
            </div>
          </div>
          <div className="col-sm-12" style={{ textAlign: 'center', margin: '15px 0' }}>
            <BooleanRadioButtons
              name="is_onsite_supplier"
              style={{ width: '187px', margin: 'auto' }}
              validate={[validator.required]}
            />
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render province/state dropdown items base on country
   * @function renderDropdownList
   */
  renderDropdownList = () => {
    const { currentCountry } = this.state;
    let dropdownList = CANADIAN_PROVINCES.map((province) => {
      return <MenuItem value={province} primaryText={province} />;
    });

    if (isEqual(currentCountry, 'United States')) {
      dropdownList = USA_STATES.map((state) => {
        return <MenuItem value={state} primaryText={state} />;
      });
    }

    return dropdownList;
  };

  /**
   * update country state when autocomplete is selected
   * @function getAddressFromAutoComplete
   * @param { address } string - passed down from autocomplete
   */
  getAddressFromAutoComplete = (address) => {
    if (!isEmpty(address)) {
      this.setState({ currentCountry: address.country });
    }
  };

  addToAddressFields = (street, city, province, country, post_code) => {
    this.props.change('street', street);
    this.props.change('city', city);
    this.props.change('province', province);
    this.props.change('country', country);
    this.props.change('postal_code', post_code);
  };

  renderVendorAddress() {
    const { streetAddress } = this.props;
    const { currentCountry } = this.state;

    return (
      <div>
        <div className="row" style={style.header}>
          <div className="col-xs-12">
            <p style={{ margin: '0', textAlign: 'center' }}>
              {' '}
              {getTranslatedString('landlordPortal.vendors.address')}{' '}
            </p>
          </div>
        </div>

        <div className="row" style={{ marginBottom: '30px', textAlign: 'center' }}>
          <div className="row">
            <div className="col-xs-4" style={{ textAlign: 'center', zIndex: '1000' }}>
              <Field
                name="unit_number"
                type="text"
                component={TextField}
                label={getTranslatedString('landlordPortal.vendors.unit')}
                style={{ fontSize: '14px', width: '100%' }}
              />
            </div>
            <div className="col-xs-8" style={{ textAlign: 'center', zIndex: '1000' }}>
              <AddressAutocomplete
                addToRedux={this.addToAddressFields}
                label={getTranslatedString('landlordPortal.vendors.street')}
                name="street"
                getAddressFromAutoComplete={this.getAddressFromAutoComplete}
                style={{ fontSize: '14px', width: '100%', marginTop: '6px' }}
                streetAddress={streetAddress}
              />
            </div>
            <div className="col-sm-6" style={{ textAlign: 'center' }}>
              <Field
                name="city"
                type="text"
                component={TextField}
                label={getTranslatedString('landlordPortal.vendors.city')}
                style={{ marginTop: '19px', fontSize: '14px', width: '100%' }}
              />
            </div>
            <div className="col-sm-6" style={{ textAlign: 'center' }}>
              {isEqual(currentCountry, 'Canada') || isEqual(currentCountry, 'United States') ? (
                <Field
                  name="province"
                  component={SelectField}
                  floatingLabelText={getTranslatedString('addressForm.division')}
                  style={{
                    fontSize: '14px',
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  {this.renderDropdownList()}
                </Field>
              ) : (
                <Field
                  name="province"
                  type="text"
                  component={TextField}
                  label={getTranslatedString('addressForm.division')}
                  style={{
                    marginTop: '19px',
                  }}
                  errorStyle={{ fontSize: '14px' }}
                />
              )}
            </div>
            <div className="col-sm-6" style={{ textAlign: 'center' }}>
              <Field
                name="postal_code"
                type="text"
                component={TextField}
                label={getTranslatedString('addressForm.area')}
                style={{ fontSize: '14px', width: '100%' }}
              />
            </div>
            <div className="col-sm-6" style={{ textAlign: 'center' }}>
              <Field
                name="country"
                type="text"
                component={TextField}
                label={getTranslatedString('landlordPortal.vendors.country')}
                style={{ fontSize: '14px', width: '100%' }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderVendorInsurance() {
    return (
      <div>
        <div className="row" style={style.header}>
          <div className="col-xs-12">
            <p style={{ margin: '0', textAlign: 'center' }}>
              {' '}
              {getTranslatedString('landlordPortal.vendors.insurance')}{' '}
            </p>
          </div>
        </div>

        <div className="row" style={{ marginBottom: '30px', textAlign: 'center' }}>
          <div className="row">
            <div className="col-xs-6" style={{ textAlign: 'center', zIndex: '1000' }}>
              <Field
                name="insurance_number"
                type="text"
                component={TextField}
                validate={validateInsuranceNumber}
                label="Insurance Number"
                style={{ fontSize: '14px', width: '100%' }}
              />
            </div>
            <div className="col-xs-6" style={{ textAlign: 'center', zIndex: '1000' }}>
              <Field
                name="insurance_expiry_date"
                component={DatePicker}
                validate={validateInsuranceExpiry}
                style={{
                  marginTop: '-15px',
                  fontSize: '14px',
                  width: '100%',
                  zIndex: 5000,
                }}
                formatDate={toFriendlyDateString}
                autoOk={true}
                textFieldStyle={{ width: '100%', fontSize: '14px' }}
                floatingLabelText="Expiration Date"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
};

AddVendorForm = reduxForm({
  form: 'add_vendor_form',
  destroyOnUnmount: true,
})(AddVendorForm);

AddVendorForm = connect((state) => ({
  streetAddress: formValueSelector('add_vendor_form')(state, 'street'),
  country: formValueSelector('add_vendor_form')(state, 'country'),
}))(AddVendorForm);

export default AddVendorForm;
